import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, user } from "@cartero/powersync";
import { patchRowVerified } from "../powersync/write-verify";

/**
 * ── Why ALL the mutations below carry `.where(eq(user.id, userId))` ─────
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this shape:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * that is, in guest mode the UPDATE went out **with no `WHERE`** → it rewrote EVERY
 * row of `user` in the local database. The branch existed for symmetry with
 * `getUserThemeSettingsQuery`, which for the guest reads with `.limit(1)` and no
 * filter; but a read `limit(1)` and an unfiltered UPDATE are not the same
 * bet: the first picks the wrong row, the second smashes all of them.
 *
 * The premise it hung on ("the guest's database only has one
 * `user` row") is false in one specific window: on the guest→real upgrade the
 * guest's database is CONNECTED to the real account's connector and
 * auto-subscribes to all the streams — including `organizations`, which syncs
 * one `user` row per EACH co-member of that account's organizations.
 * There the table has 1 + 1 + N rows, `user` is NOT `localOnly`, and every row
 * touched enqueues its own op in `ps_crud`.
 *
 * The correct filter is NOT a guess: the guest's row is seeded by
 * `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) with
 * `db.insert(user).values({ id: uid, … })`, and for a guest tab `uid`
 * is exactly `VISITOR_ID`. So `eq(user.id, userId)` points at the SAME
 * row that the unfiltered branch meant to touch when the table had a single
 * row — identical behaviour in the normal case, bounded in the rest. It is
 * also the shape `updateUserFontFamilyMutation` and
 * `updateUserImageMutation` were already using, which never had a guest branch.
 *
 * Guarded by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 */

/**
 * ── Why they ALL go through `patchRowVerified` (§4.11) ────────────────
 *
 * `user` is NOT excluded from the "ghost UPDATE" just because "the server
 * creates it" — that premise comes from the canon, not from this repo. And since 2026-08-13
 * (`apps/web/src/providers/theme-provider.tsx`, guard `uid !== VISITOR_ID`)
 * `ThemeProvider` seeds locally for ANY account either:
 * account: its `db.insert(user).values({ id: uid, … })` now only fires
 * for the guest. Even so the protection of the 6 is still needed, for
 * two distinct reasons, neither of which depends on who does the INSERT:
 *
 *   1. The guest (`VISITOR_ID`) STILL receives that local seed on every
 *      cold start (there is no server to bring it the row). Right
 *      afterwards, the guest may change theme/blur/language from
 *      `/profile` or `/settings` — the same UPDATE-by-id against a row whose
 *      INSERT may still be in the CRUD queue, except now bounded to a single
 *      `uid` instead of anyone. Measured in
 *      `__tests__/theme-provider-seed-window.test.ts`.
 *   2. For a real account, the server creates the row (§ above,
 *      `user.rs:118,367`) — but it reaches the client over SYNC, not instantly:
 *      until the initial checkpoint downloads that row,
 *      PowerSync's local view may not reflect it yet. `write-verify.ts` (§4.11)
 *      documents that the "ghost UPDATE" window is "the CRUD queue drained
 *      in between", not "who inserted" — a user editing their profile
 *      right after sign-up/login, before that first checkpoint settles,
 *      triggers the same case with no local INSERT involved at all.
 *
 * In short: closing the ghost seed of `ThemeProvider` for real accounts
 * removes ONE trigger of the window (the most frequent one in practice), not the
 * mechanism — which is inherent to how PowerSync rebuilds the view from the
 * checkpoint, not to this repo. That is why the 6 mutations stay wrapped.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
): Promise<void> {
  await patchRowVerified(
    () => db.update(user).set({ locale }).where(eq(user.id, userId)),
    async () => (await db.select().from(user).where(eq(user.id, userId)).limit(1))[0],
    { locale },
  );
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
): Promise<void> {
  await patchRowVerified(
    () => db.update(user).set({ font_family: fontFamilyId }).where(eq(user.id, userId)),
    async () => (await db.select().from(user).where(eq(user.id, userId)).limit(1))[0],
    { font_family: fontFamilyId },
  );
}

/**
 * Writes the avatar KEY into the local row.
 *
 * It is written local-first (just like language, font and theme) and not over REST: that way
 * the new photo shows up instantly and survives a cold reload with no
 * connection. The BINARY does go over REST (`POST /api/avatar`) because it does not fit in the
 * sync queue; what travels through here is only the key that upload returns.
 *
 * `null` = "remove the photo" (the initials come back).
 *
 * The guest has no syncable row of its own nor can have an avatar (rule
 * zero), so there is no `VISITOR_ID` branch here as in `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
): Promise<void> {
  await patchRowVerified(
    () => db.update(user).set({ image: imageKey }).where(eq(user.id, userId)),
    async () => (await db.select().from(user).where(eq(user.id, userId)).limit(1))[0],
    { image: imageKey },
  );
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
): Promise<void> {
  await patchRowVerified(
    () => db.update(user).set({ global_theme_id: themeId }).where(eq(user.id, userId)),
    async () => (await db.select().from(user).where(eq(user.id, userId)).limit(1))[0],
    { global_theme_id: themeId },
  );
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
): Promise<void> {
  const is_blur = isBlur ? 1 : 0;
  await patchRowVerified(
    () => db.update(user).set({ is_blur }).where(eq(user.id, userId)),
    async () => (await db.select().from(user).where(eq(user.id, userId)).limit(1))[0],
    { is_blur },
  );
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
): Promise<void> {
  const follow_device_theme = followDevice ? 1 : 0;
  await patchRowVerified(
    () => db.update(user).set({ follow_device_theme }).where(eq(user.id, userId)),
    async () => (await db.select().from(user).where(eq(user.id, userId)).limit(1))[0],
    { follow_device_theme },
  );
}
